import { Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import Divider from "@mui/material/Divider";

import { useForm } from "react-hook-form";
import PropTypes from "prop-types";

import PaymentIcon from "@mui/icons-material/Payment";
import Customer from "./Customer";

import { useRefund } from "../hooks";
import React, { useEffect } from "react";
import SoftBox from "../../../components/SoftBox";
import pxToRem from "../../../assets/theme/functions/pxToRem";
import SoftTypography from "../../../components/SoftTypography";
import FormProvider from "../../../components/FormProviders";
import SoftButton from "../../../components/SoftButton";
import { TextInputField } from "../../../components/FormField";
import StatusChip from "../../../components/StatusChip";
import _ from "lodash";

const sanitizeRequest = ({ amount }) => {
  return _.pickBy(
    {
      amount: amount && typeof amount === "number" ? amount : parseFloat(amount),
    },
    _.identity
  );
};
const RefundModal = ({ isOpen, onClose, transaction }) => {
  //form provider
  const methods = useForm();
  const { handleSubmit, setValue } = methods;
  // //hooks
  const { isLoading, mutate } = useRefund();
  // // create a function that handles the form submission
  const onSubmit = ({ amount }) => {
    mutate(
      { transactionId: _.get(transaction, "id"), ...sanitizeRequest({ amount }) },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  useEffect(() => {
    setValue("amount", _.get(transaction, "amount"));
  }, [transaction]);

  /** Stacks the details on small screens and lets long references wrap within each column. */
  const renderTransactionDetail = () => (
    <Grid item xs={12} sm={12}>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item sm={2} sx={{ display: { xs: "none", sm: "block" } }}>
            <PaymentIcon
              sx={{
                width: pxToRem(48),
                height: pxToRem(48),
                color: (theme) => theme.palette.secondary.main,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={5} sx={{ minWidth: 0 }}>
            <SoftBox
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              <SoftTypography variant="caption">
                <b>Type:</b> &nbsp;{_.get(transaction, "type")}
              </SoftTypography>
              <SoftTypography variant="caption">
                <b>Amount:</b> &nbsp;{_.get(transaction, "amount")}
              </SoftTypography>
              <SoftTypography variant="caption">
                <b>Currency:</b> &nbsp;{_.get(transaction, "currency")}
              </SoftTypography>
              <SoftTypography variant="caption">
                <b>Country:</b> &nbsp;{_.get(transaction, "country")}
              </SoftTypography>
              <SoftTypography variant="caption">
                <b>Payment Option:</b> &nbsp;{_.get(transaction, "paymentOption")}
              </SoftTypography>
              <SoftTypography variant="caption">
                <b>Merchant Reference:</b> &nbsp;
                {_.get(transaction, "merchantReference")}
              </SoftTypography>
            </SoftBox>
          </Grid>
          <Grid item xs={12} sm={5} sx={{ minWidth: 0 }}>
            <SoftBox
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              <SoftTypography variant="caption">
                <b>Status:</b> &nbsp; <StatusChip label={_.get(transaction, "status")} />
              </SoftTypography>
              <SoftTypography component="div" variant="caption" sx={{ width: "100%" }}>
                <b>Customer:</b> &nbsp;{" "}
                <Customer
                  customer={_.get(transaction, "customer", {})}
                  sx={{ minWidth: 0, maxWidth: "100%" }}
                />
              </SoftTypography>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </Grid>
  );

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            m: 2,
            width: "calc(100% - 32px)",
            maxHeight: "calc(100dvh - 32px)",
            overflow: "hidden",
            // Keep the title and actions fixed while only DialogContent scrolls.
            "& > form": { display: "flex", flexDirection: "column", minHeight: 0 },
          },
        }}
      >
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Refund Transaction</DialogTitle>
          <DialogContent sx={{ minHeight: 0, overflowWrap: "anywhere" }}>
            <SoftBox p={{ xs: 0, sm: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                  <SoftTypography variant="h6" fontWeight="medium">
                    Refund
                  </SoftTypography>
                  <SoftTypography variant="button" color="text" fontWeight="regular">
                    Are you sure you want to refund this transaction{" "}
                    <b>{_.get(transaction, "id")}</b>?
                  </SoftTypography>
                </Grid>
                {renderTransactionDetail()}
                <Grid item xs={12}>
                  <Divider />
                  {/* Keep the field's percentage height independent of the divider and grid cell. */}
                  <SoftBox>
                    <TextInputField
                      name="amount"
                      label="Amount"
                      type="number"
                      inputProps={{
                        maxLength: 13,
                        step: "0.0001",
                      }}
                    />
                  </SoftBox>
                </Grid>
              </Grid>
            </SoftBox>
          </DialogContent>
          <DialogActions>
            <SoftButton variant="gradient" onClick={onClose}>
              Cancel
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="info"
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
            >
              Refund
            </SoftButton>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </>
  );
};

// Setting default values for the props of RefundModal
RefundModal.defaultProps = {
  isOpen: false,
};

// Typechecking props for the RefundModal
RefundModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  transaction: PropTypes.object,
};

export default RefundModal;
